import { extractEntityIdFromDataId, extractNumericEntityIdFromDataId } from './treeUtils';
import { TreeNodeFactory } from './treeNodeFactory';
import { TreeNode } from '@/types/tree';
import { IBackend } from '@/api/BackendApi';
import { ApiException } from '@/api/ApiClient';
import { log } from '@/lib/utils/logger';
import { invalidateSampleGridCache } from '@/lib/cache/sampleGridCache';
import { getProblemDetail } from '@/lib/utils/apiErrors';

/**
 * Interface for delete operation dependencies
 */
export interface IDeleteDependencies {
  showSnackbar: (
    message: string,
    severity?: 'success' | 'error' | 'warning' | 'info',
    autoHideDuration?: number | null,
    title?: string
  ) => void;
  showConfirmDialog: (config: {
    title: string;
    message: string;
    confirmText?: string;
    isDestructive?: boolean;
  }) => Promise<boolean>;
  getData?: (dataId: string) => any;
}

/**
 * Performs the actual delete operation based on node type
 * This is the core deletion logic shared between useDeleteEntity and actionHandlers
 */
export async function performDeleteOperation(node: TreeNode, backend: IBackend, force: boolean = false): Promise<void> {
  switch (node.type) {
    case 'sample': {
      await backend.sample.deletePending(extractEntityIdFromDataId(node.dataId).toString(), force);
      invalidateSampleGridCache();
      break;
    }
    case 'sampleActivity': {
      await backend.sampleActivity.deletePending(extractNumericEntityIdFromDataId(node.dataId), force);
      break;
    }
    case 'project': {
      await backend.project.delete(extractNumericEntityIdFromDataId(node.dataId));
      break;
    }
    case 'publication': {
      await backend.publication.delete(extractNumericEntityIdFromDataId(node.dataId).toString());
      break;
    }
    case 'projectActivity': {
      await backend.projectActivity.deletePending(extractNumericEntityIdFromDataId(node.dataId), force);
      break;
    }
    case 'theoryActivity': {
      await backend.theoryActivity.deletePending(extractNumericEntityIdFromDataId(node.dataId), force);
      break;
    }
    case 'communication': {
      await backend.communication.delete(extractNumericEntityIdFromDataId(node.dataId));
      break;
    }
    case 'dataPackage': {
      await backend.data.deletePending(extractNumericEntityIdFromDataId(node.dataId).toString());
      break;
    }
    case 'shipping': {
      await backend.shipping.delete(extractNumericEntityIdFromDataId(node.dataId));
      break;
    }
    default:
      throw new Error(`Unsupported node type: ${node.type}`);
  }

  log.debug('[deleteEntityUtils] Node deleted successfully:', {
    nodeId: node.nodeId,
    type: node.type,
    force,
  });
}

/**
 * Handles delete errors with proper error codes (428, 424)
 * Returns true if deletion should be retried with force, false if it should be aborted
 */
export async function handleDeleteError(
  error: unknown,
  node: TreeNode,
  deps: IDeleteDependencies
): Promise<{ shouldRetry: boolean; shouldAbort: boolean }> {
  if (!ApiException.isApiException(error)) {
    deps.showSnackbar('Unexpected error occurred while deleting.', 'error');
    return { shouldRetry: false, shouldAbort: true };
  }

  if (error.status === 428) {
    // Requires confirmation - show additional dialog
    const entityTypeLabel = TreeNodeFactory.getNodeTypeLabel(node, deps.getData);
    const forceConfirmed = await deps.showConfirmDialog({
      title: `Force Delete ${entityTypeLabel}`,
      message:
        getProblemDetail(error) ||
        `This ${entityTypeLabel} has dependencies. Are you sure you want to force delete it?`,
      confirmText: 'Force Delete',
      isDestructive: true,
    });

    if (forceConfirmed) {
      return { shouldRetry: true, shouldAbort: false };
    }
    return { shouldRetry: false, shouldAbort: true };
  } else if (error.status === 424) {
    // Cannot delete - show detailed error message
    const entityTypeLabel = TreeNodeFactory.getNodeTypeLabel(node, deps.getData);
    const errorMessage = getProblemDetail(error) || `${entityTypeLabel} ${node.label} cannot be deleted.`;
    deps.showSnackbar(
      errorMessage,
      'error',
      null, // do not hide automatically, leave to user to close manually
      undefined // No title needed - the message itself explains why
    );
    return { shouldRetry: false, shouldAbort: true };
  } else {
    deps.showSnackbar(getProblemDetail(error) ?? 'Failed to delete.', 'error');
    return { shouldRetry: false, shouldAbort: true };
  }
}
